import { useState } from 'react';
import {
  ActionIcon, Alert, Badge, Button, Chip, Group, SegmentedControl, Stack, Table, Text, TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconCalendarCog, IconPlus, IconTrash, IconInfoCircle } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { SectionCard } from './SectionCard';
import { uzDateProps } from '../lib/dateInput';
import { confirmDelete } from '../lib/confirm';

interface Holat {
  month: string;
  workDays: string;
  ishKunlari: number;
  bayramKunlari: number;
  bayramlar: { id: string; date: string; name: string }[];
}

interface Bayramlar { year: number; items: { id: string; date: string; name: string }[] }

const kunKalit = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
/** "2026-03-21" → "21.03.2026" (satrdan — Date orqali o'tkazilmaydi, mintaqa siljitmaydi) */
const sanaKorinish = (k: string) => `${k.slice(8, 10)}.${k.slice(5, 7)}.${k.slice(0, 4)}`;

/**
 * ISH KUNLARI VA BAYRAMLAR.
 *
 * Hafta kunlari — dars va davomat kunlari: o'quvchi davomati va xodim davomati
 * (qo'lda kelish-ketish, FACE ID). Oylik maoshning kunlik qiymati bu yerdan EMAS —
 * "Maosh qoidasi → Maosh ish kunlari" (`PayrollRuleSettings`, `payrollWorkDays`) dan;
 * qoida yoqilgan oyda xodim davomati o'sha kunlarda ham olinadi.
 *
 * Bayram ALOHIDA ro'yxat va IKKALASIGA ham ta'sir qiladi: Navro'z dushanbaga to'g'ri
 * kelsa, u hafta kuni bo'yicha ish kuni bo'lib qolardi. Bayram ish kunidan chiqarilmasa,
 * o'sha oyda bir kunlik maosh sun'iy past chiqadi (oylik bir xil, bo'luvchi esa katta).
 */
export function WorkDaysSettings() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const bugun = kunKalit(new Date());
  // Mahalliy (Toshkent) oy — toISOString UTC beradi va oy oxirida bir oy orqada qolardi
  const oy = bugun.slice(0, 7);
  const joriyYil = Number(bugun.slice(0, 4));
  /* Bayramlar ro'yxati YIL bo'yicha. Ilgari faqat joriy oyning bayramlari ko'rinardi:
     boshqa oyga (masalan martdagi Navro'zga) qo'shilgan sana saqlanardi-yu, uni na
     ko'rib, na o'chirib bo'lardi — "Bu oyda belgilangan sana yo'q" chiqib turardi. */
  const [yil, setYil] = useState(joriyYil);

  const { data: holat } = useQuery({
    queryKey: ['workdays', oy],
    queryFn: async () => (await api.get<Holat>('/payroll/workdays', { params: { month: oy } })).data,
  });

  const { data: bayramlar } = useQuery({
    queryKey: ['holidays', yil],
    queryFn: async () => (await api.get<Bayramlar>('/payroll/holidays', { params: { year: yil } })).data,
  });

  const [yangiSana, setYangiSana] = useState<Date | null>(null);
  const [yangiNom, setYangiNom] = useState('');

  const yangila = () => {
    qc.invalidateQueries({ queryKey: ['workdays'] });
    qc.invalidateQueries({ queryKey: ['holidays'] });
    /* Maosh hisobi ham bog'liq (bayramlar — maosh kunlari bo'luvchisi, dars kunlari —
       qoida yoqilmagan oyda kunlik va xodim davomati) — eski qiymat ekranda qolib ketmasin. */
    qc.invalidateQueries({ queryKey: ['salary-compute'] });
    qc.invalidateQueries({ queryKey: ['payroll-summary'] });
    qc.invalidateQueries({ queryKey: ['payroll-oylik'] });
  };
  const xato = (e: unknown) =>
    notifications.show({
      color: 'red',
      message: (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('common.error'),
    });

  const saqla = useMutation({
    mutationFn: (workDays: string) => api.put('/payroll/workdays', { workDays }),
    onSuccess: () => { notifications.show({ color: 'green', message: t('common.save') }); yangila(); },
    onError: xato,
  });

  const bayramQosh = useMutation({
    mutationFn: () => api.post('/payroll/holidays', { date: yangiSana ? kunKalit(yangiSana) : '', name: yangiNom.trim() }),
    onSuccess: () => {
      // Qo'shilgan sana boshqa yilda bo'lsa — ro'yxat o'sha yilga o'tadi, natija ko'rinsin
      if (yangiSana) setYil(yangiSana.getFullYear());
      setYangiSana(null); setYangiNom(''); yangila();
    },
    onError: xato,
  });

  const bayramOchir = useMutation({
    mutationFn: (id: string) => api.delete(`/payroll/holidays/${id}`),
    onSuccess: yangila,
    onError: xato,
  });

  const tanlangan = (holat?.workDays ?? '').split(',').filter(Boolean);
  const KUNLAR = [
    { value: '1', label: t('workDays.mon') },
    { value: '2', label: t('workDays.tue') },
    { value: '3', label: t('workDays.wed') },
    { value: '4', label: t('workDays.thu') },
    { value: '5', label: t('workDays.fri') },
    { value: '6', label: t('workDays.sat') },
    { value: '7', label: t('workDays.sun') },
  ];

  return (
    <SectionCard title={t('workDays.section')} subtitle={t('workDays.sectionDesc')} icon={IconCalendarCog} maw={520} mb="md">
      <Stack gap="sm">
        <Chip.Group
          multiple
          value={tanlangan}
          onChange={(v) => {
            /* Bo'sh ro'yxat SAQLANMAYDI: hech qachon ish kuni bo'lmay qolardi —
               davomat butunlay to'xtardi va kunlik maosh nolga bo'linardi.
               Server ham rad etadi, lekin bu yerda oldini olgan ma'qul. */
            if (!v.length) return;
            saqla.mutate([...v].sort().join(','));
          }}
        >
          <Group gap={6}>
            {KUNLAR.map((k) => (
              <Chip key={k.value} value={k.value} size="sm" disabled={saqla.isPending}>
                {k.label}
              </Chip>
            ))}
          </Group>
        </Chip.Group>

        {holat && (
          <Alert variant="light" color="blue" icon={<IconInfoCircle size={16} />} p="xs">
            <Text size="sm">
              {t('workDays.thisMonth', { oy: holat.month, kun: holat.ishKunlari })}
              {holat.bayramKunlari > 0 && ` · ${t('workDays.holidaysExcluded', { n: holat.bayramKunlari })}`}
            </Text>
          </Alert>
        )}

        <Group justify="space-between" align="center" mt="xs" gap="xs">
          <Text size="sm" fw={500}>{t('workDays.holidays')}</Text>
          <SegmentedControl
            size="xs"
            value={String(yil)}
            onChange={(v) => setYil(Number(v))}
            data={[joriyYil - 1, joriyYil, joriyYil + 1].map((y) => ({ value: String(y), label: String(y) }))}
          />
        </Group>
        {!bayramlar?.items.length ? (
          <Text size="xs" c="dimmed">{t('workDays.holidaysEmpty', { yil })}</Text>
        ) : (
          <Table verticalSpacing={4}>
            <Table.Tbody>
              {bayramlar.items.map((b) => {
                const otgan = b.date < bugun;
                return (
                  <Table.Tr key={b.id} style={otgan ? { opacity: 0.55 } : undefined}>
                    <Table.Td w={110}><Text size="sm" ff="monospace">{sanaKorinish(b.date)}</Text></Table.Td>
                    <Table.Td><Text size="sm">{b.name}</Text></Table.Td>
                    <Table.Td w={36}>
                      <ActionIcon
                        variant="subtle" color="red" size="sm"
                        loading={bayramOchir.isPending && bayramOchir.variables === b.id}
                        onClick={() => confirmDelete(`${sanaKorinish(b.date)} — ${b.name}`, () => bayramOchir.mutate(b.id))}
                        aria-label={t('common.delete')}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}

        <Group align="flex-end" gap="xs">
          <DateInput {...uzDateProps} label={t('workDays.date')} value={yangiSana} onChange={setYangiSana} w={150} />
          <TextInput
            label={t('workDays.name')}
            placeholder={t('workDays.namePlaceholder')}
            value={yangiNom}
            onChange={(e) => setYangiNom(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Button
            variant="light"
            leftSection={<IconPlus size={14} />}
            loading={bayramQosh.isPending}
            disabled={!yangiSana || !yangiNom.trim()}
            onClick={() => bayramQosh.mutate()}
          >
            {t('common.add')}
          </Button>
        </Group>

        <Text size="xs" c="dimmed">
          {t('workDays.note')} <Badge size="xs" variant="light">{t('workDays.affects')}</Badge>
        </Text>
      </Stack>
    </SectionCard>
  );
}
